// Utility functions for parsing and formatting dates and times

import { formatISO, parseISO } from "date-fns";

import { parseRobustDate } from "./date-helpers.ts";

/**
 * Parses a date string in ISO format (YYYY-MM-DD) to a Date object
 * @param dateString - The date string to parse (e.g., "2025-08-18")
 * @returns Date object or null if parsing fails
 */
export const parseDateString = (dateString: string | null): Date | null => {
	return parseRobustDate(dateString);
};

/**
 * Parses a time string to a Date object.
 * Supports "HH:MM AM/PM" format and ISO date strings.
 * @param timeString - The time string to parse (e.g., "12:30 PM")
 * @returns Date object or null if parsing fails
 */
export const parseTimeString = (
	timeString: string | null | undefined,
): Date | null => {
	if (!timeString) return null;

	// Try "HH:MM AM/PM" format
	const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec(timeString);
	if (match?.[1] && match[2]) {
		let hours = Number.parseInt(match[1], 10);
		const minutes = Number.parseInt(match[2], 10);
		const period = match[3]?.toUpperCase();

		if (period === "PM" && hours !== 12) hours += 12;
		else if (period === "AM" && hours === 12) hours = 0;

		const date = new Date();
		date.setHours(hours, minutes, 0, 0);
		return date;
	}

	// Try ISO or other Date-parseable format
	const date = new Date(timeString);
	return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Formats a Date (or date string) to "HH:MM AM/PM" format.
 * @param time - The Date object or date string to format
 * @returns Formatted time string (e.g., "12:30 PM") or empty string if invalid
 */
export const formatTimeToString = (
	time: Date | string | null | undefined,
): string => {
	if (!time) return "";

	const date = typeof time === "string" ? new Date(time) : time;
	if (Number.isNaN(date.getTime())) {
		if (typeof time === "string") {
			// If it's already a formatted time (like "08:00 AM"), return it if it's within API limits.
			// Legacy addresses may have corrupted data which fails API validation.
			return time.length <= 10 ? time : "";
		}
		return "";
	}

	const hours = date.getHours();
	const minutes = date.getMinutes();
	const period = hours >= 12 ? "PM" : "AM";
	const displayHours = hours % 12 || 12;

	return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

/**
 * Normalizes an hours string to the "H:MM AM/PM" format the quote API expects.
 *
 * Handles two shapes of legacy/saved address data that the quote API rejects with a
 * "not in the correct 'H:MM AM/PM' format" error:
 *   - "H:MM AM/PM" values with a leading zero on the hour (e.g. "08:00 AM" -> "8:00 AM").
 *   - 24-hour values with no meridiem (e.g. "08:00" -> "8:00 AM", "17:00" -> "5:00 PM").
 *
 * The result mirrors how hours are displayed in the UI (no leading zeros, uppercase
 * meridiem). Values that don't match either pattern are returned unchanged so
 * unrelated formats are left intact.
 *
 * @param hours - The hours string to normalize (e.g. "08:00 AM" or "17:00")
 * @returns Normalized hours string (e.g. "8:00 AM"), or the input unchanged
 */
export const normalizeHoursToApiFormat = (
	hours: string | null | undefined,
): string => {
	if (!hours) return "";

	const trimmed = hours.trim();
	if (!trimmed) return "";

	// "H:MM AM/PM" -> strip leading zero on the hour, uppercase the meridiem.
	const meridiemMatch = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(trimmed);
	if (meridiemMatch?.[1] && meridiemMatch[2] && meridiemMatch[3]) {
		const hour = Number.parseInt(meridiemMatch[1], 10);
		return `${hour}:${meridiemMatch[2]} ${meridiemMatch[3].toUpperCase()}`;
	}

	// "HH:MM" (24-hour, no meridiem) -> convert to 12-hour "H:MM AM/PM".
	const twentyFourHourMatch = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
	if (twentyFourHourMatch?.[1] && twentyFourHourMatch[2]) {
		const hour24 = Number.parseInt(twentyFourHourMatch[1], 10);
		const minutes = Number.parseInt(twentyFourHourMatch[2], 10);
		if (hour24 >= 0 && hour24 <= 23 && minutes >= 0 && minutes <= 59) {
			const period = hour24 >= 12 ? "PM" : "AM";
			const hour12 = hour24 % 12 || 12;
			return `${hour12}:${twentyFourHourMatch[2]} ${period}`;
		}
	}

	return trimmed;
};

/**
 * Gets the short name of the timezone (e.g., "EST", "PST")
 * @param date - Optional date to determine the timezone offset/name for (defaults to now)
 * @returns Short timezone name
 */
export const getTimeZoneShortName = (date: Date = new Date()): string => {
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZoneName: "short",
	}).formatToParts(date);
	const timeZoneName = parts.find((part) => part.type === "timeZoneName");
	return timeZoneName ? timeZoneName.value : "";
};

/**
 * Parses an ISO 8601 string to a Date object.
 * @param value - The ISO string to parse
 * @returns Date object, or null if empty or invalid
 */
export const parseIsoDate = (value?: string | null): Date | null => {
	if (!value) {
		return null;
	}
	const parsed = parseISO(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Formats a Date to an ISO 8601 string.
 * @param value - The Date to format
 * @returns ISO string, or null when no date is provided
 */
export const toIsoString = (value: Date | null): string | null =>
	value ? formatISO(value) : null;

/**
 * Combines the calendar day from one Date with the time of day from another,
 * so separate date and time pickers can write to a single value.
 * @param datePart - Date supplying the calendar day
 * @param timePart - Date supplying the hours and minutes
 * @returns Merged Date, or null when both parts are missing
 */
export const mergeDateTime = (
	datePart: Date | null,
	timePart: Date | null,
): Date | null => {
	const source = datePart ?? timePart;
	if (!source) {
		return null;
	}
	const base = new Date(source);
	if (timePart) {
		base.setHours(timePart.getHours(), timePart.getMinutes(), 0, 0);
	}
	return base;
};
